import clamp from 'lodash/clamp';
import { FenwickTree } from '../utils/FenwickTree';

export interface LogicalPos {
    pageIndex: number;
    offsetPercent: number; // 0-1
}

export interface LayoutState {
    totalHeight: number;
    visibleRange: { start: number; end: number };
}

/**
 * LayoutStore: 逻辑布局引擎核心
 * 负责页高管理、坐标换算、锚点计算
 */
export class LayoutStore {
    private bit: FenwickTree;
    private unscaledHeights: Float64Array;
    private isMeasured: Uint8Array;
    private totalPages: number;
    private defaultHeight: number;

    // 订阅列表
    private listeners: Set<(state: LayoutState) => void> = new Set();

    // rAF 批量通知句柄（替代 debounce(16ms)，语义更精确）
    private rafHandle: number | null = null;

    // 静默批量写入计数器：> 0 时 updateHeight 不触发 notify
    private silentCount = 0;

    constructor(totalPages: number, defaultHeight: number = 800) {
        this.totalPages = totalPages;
        this.defaultHeight = defaultHeight;
        this.bit = new FenwickTree(totalPages);
        this.unscaledHeights = new Float64Array(totalPages);
        this.isMeasured = new Uint8Array(totalPages);

        // 初始化默认高度
        for (let i = 0; i < totalPages; i++) {
            this.unscaledHeights[i] = defaultHeight;
            this.bit.add(i, defaultHeight);
        }
    }

    /**
     * 更新页面高度 (Scale=1)，通过 rAF 批量触发通知（同帧内多次调用合并为一次）
     */
    updateHeight(index: number, height: number): boolean {
        if (!this.applyHeight(index, height)) return false;
        if (this.silentCount === 0) {
            this.scheduleUpdate();
        }
        return true;
    }

    /**
     * 静默写入页高：不触发 notify，适合初始化批量预取。
     * 批量写入完成后调用 notifyOnce() 一次性刷新。
     */
    updateHeightSilent(index: number, height: number): boolean {
        return this.applyHeight(index, height);
    }

    /**
     * 批量静默写入辅助：在 callback 内的所有 updateHeight 调用均静默，
     * callback 结束后触发一次 notify。
     *
     * @example
     * store.batchUpdate(() => {
     *   pages.forEach((h, i) => store.updateHeight(i, h));
     * });
     */
    batchUpdate(callback: () => void): void {
        this.silentCount++;
        try {
            callback();
        } finally {
            this.silentCount--;
            if (this.silentCount === 0) {
                this.notifyOnce();
            }
        }
    }

    /**
     * 立即触发一次通知（不等下一帧），用于批量写入完成后的单次刷新
     */
    notifyOnce(): void {
        if (this.rafHandle !== null) {
            cancelAnimationFrame(this.rafHandle);
            this.rafHandle = null;
        }
        this.flushNotify();
    }

    /** 核心高度写入逻辑（不触发通知） */
    private applyHeight(index: number, height: number): boolean {
        if (index < 0 || index >= this.totalPages) return false;
        if (!Number.isFinite(height) || height <= 0) return false;

        const oldHeight = this.unscaledHeights[index];
        const diff = height - oldHeight;

        if (Math.abs(diff) < 0.1) return false; // 忽略微小变化

        this.unscaledHeights[index] = height;
        this.isMeasured[index] = 1;
        this.bit.add(index, diff);
        return true;
    }

    /** 调度下一帧通知（同帧内多次调用只触发一次） */
    private scheduleUpdate(): void {
        if (this.rafHandle !== null) return;
        this.rafHandle = requestAnimationFrame(() => {
            this.rafHandle = null;
            this.flushNotify();
        });
    }

    private flushNotify(): void {
        const state: LayoutState = {
            totalHeight: this.getTotalHeight(1),
            visibleRange: { start: 0, end: 0 },
        };
        for (const l of Array.from(this.listeners)) l(state);
    }

    /**
     * 获取逻辑坐标对应的物理位置 (scrollTop)
     */
    getPhysicalTop(logicalPos: LogicalPos, scale: number): number {
        const { pageIndex, offsetPercent } = logicalPos;
        const pageTop = this.bit.query(pageIndex - 1) * scale;
        const pageHeight = this.unscaledHeights[pageIndex] * scale;
        return pageTop + pageHeight * offsetPercent;
    }

    /**
     * 获取物理位置对应的逻辑坐标
     */
    getLogicalPos(scrollTop: number, viewportHeight: number, scale: number): LogicalPos {
        const centerTop = scrollTop + viewportHeight / 2;
        const unscaledCenterTop = centerTop / scale;

        const pageIndex = clamp(this.bit.findFirstGreaterOrEqual(unscaledCenterTop), 0, this.totalPages - 1);

        const pageTop = this.bit.query(pageIndex - 1) * scale;
        const pageHeight = this.unscaledHeights[pageIndex] * scale;
        // 使用 centerTop 计算偏移，确保补偿逻辑 (centerTop 为锚点) 的一致性
        const offsetInPage = centerTop - pageTop;
        const offsetPercent = clamp(offsetInPage / pageHeight, 0, 1);

        return { pageIndex, offsetPercent };
    }

    /**
     * 获取可见范围
     */
    getVisibleRange(scrollTop: number, viewportHeight: number, scale: number, overscanPx: number = 1000) {
        const startY = Math.max(0, (scrollTop - overscanPx) / scale);
        const endY = (scrollTop + viewportHeight + overscanPx) / scale;

        const startIndex = clamp(this.bit.findFirstGreaterOrEqual(startY), 0, this.totalPages - 1);
        const endIndex = clamp(this.bit.findFirstGreaterOrEqual(endY) + 1, startIndex + 1, this.totalPages);

        return { start: startIndex, end: endIndex };
    }

    /**
     * 获取指定页面的物理 Top
     */
    getPageTop(index: number, scale: number): number {
        return this.bit.query(index - 1) * scale;
    }

    /**
     * 获取指定页面的物理高度
     */
    getPageHeight(index: number, scale: number): number {
        return this.unscaledHeights[index] * scale;
    }

    /**
     * Debug helpers (no behavior impact)
     */
    isPageMeasured(index: number): boolean {
        if (index < 0 || index >= this.totalPages) return false;
        return this.isMeasured[index] === 1;
    }

    getUnscaledHeight(index: number): number {
        if (index < 0 || index >= this.totalPages) return 0;
        return this.unscaledHeights[index];
    }

    getDefaultHeight(): number {
        return this.defaultHeight;
    }

    /**
     * 获取总高度
     */
    getTotalHeight(scale: number): number {
        return this.bit.query(this.totalPages - 1) * scale;
    }

    /**
     * 订阅变更
     */
    subscribe(listener: (state: LayoutState) => void) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    /**
     * 销毁资源
     */
    dispose() {
        if (this.rafHandle !== null) {
            cancelAnimationFrame(this.rafHandle);
            this.rafHandle = null;
        }
        this.listeners.clear();
        this.bit.clear();
    }
}
